import { useRef, useState, useCallback, useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const CONNECT_TIMEOUT_SECONDS = parseInt(process.env.REACT_APP_CALL_CONNECT_TIMEOUT_SECONDS || "20", 10);

/**
 * iter56 — WebRTC observability beacon.
 * Fires console.log AND a fire-and-forget POST to /api/debug/webrtc-log so the
 * server-side trace endpoint can reconstruct the full call timeline from one
 * place. Intentionally never throws / never blocks signaling.
 */
function _track(sessionId, role, event, data = {}) {
  try {
    // eslint-disable-next-line no-console
    console.log(`[FRONTEND_${event}]`, { sessionId, role, ...data });
  } catch (_) { /* noop */ }
  if (!sessionId) return;
  try {
    const body = JSON.stringify({
      session_id: sessionId,
      role: role || "",
      event,
      device_info: typeof navigator !== "undefined" ? (navigator.userAgent || "") : "",
      data: data || {},
      ts: new Date().toISOString(),
    });
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(`${API}/api/debug/webrtc-log`, blob);
    } else if (typeof fetch !== "undefined") {
      fetch(`${API}/api/debug/webrtc-log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) { /* never block on telemetry */ }
}

function _envInfo() {
  let platform = "";
  try { platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || ""; } catch (_) { /* noop */ }
  let network_type = null;
  let connection_kind = null;
  try {
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c) { network_type = c.effectiveType || null; connection_kind = c.type || null; }
  } catch (_) { /* noop */ }
  return { platform, network_type, connection_kind };
}

/**
 * Minimal WebRTC voice-call hook for AvisoCar.
 * - Anonymous peer-to-peer audio via a signaling WebSocket.
 * - No identity, phone, or name is sent over the wire.
 *
 * Phases: idle | ringing | connecting | connected | ended | failed
 *
 * iter61 ROOT-CAUSE FIX #1: previously ws.onopen/onmessage were assigned
 * AFTER `await getUserMedia()` (1-2s on iPhone). The WS handshake finished
 * during mic acquisition, so the `open` event fired with NO handler and was
 * lost forever → the offer was never created → zero negotiation. Now ALL WS
 * handlers are attached synchronously right after `new WebSocket(...)`, and
 * the offer waits for BOTH ws-open AND mic-ready.
 */
export default function useAnonymousCall({
  sessionId,
  role,            // "scanner" | "owner"
  token = "",      // JWT for owner role (dashboard-authenticated)
  access = "",     // magic-link access token for owner role (no-login)
  autoStart = false,
}) {
  const [phase, setPhase] = useState("idle");
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState(null);
  const [durationSec, setDurationSec] = useState(0);
  const [health, setHealth] = useState({
    connectionState: null,
    iceState: null,
    iceSource: null,
    candidateType: null,
    turnUsed: false,
    localAudioLive: false,
    remoteAudioReceived: false,
    rttMs: null,
    jitterMs: null,
    packetsLost: null,
    bitrateKbps: null,
  });

  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const pendingIceRef = useRef([]);
  const startedAtRef = useRef(null);
  const timerRef = useRef(null);
  const peerReadyRef = useRef(false);
  const statsTimerRef = useRef(null);
  const lastStatsRef = useRef({ bytes: 0, ts: 0 });
  // iter61 refs
  const phaseRef = useRef("idle");
  const connectTimeoutRef = useRef(null);
  const wsOpenRef = useRef(false);
  const micReadyRef = useRef(false);
  const offerSentRef = useRef(false);
  const pendingRemoteOfferRef = useRef(null);
  // iter71 3.3 — single automatic ICE restart before surrendering
  const iceRestartTriedRef = useRef(false);
  // Bloque 1 — [ICE_CANDIDATE_TYPE] + [CALL_SUMMARY] telemetry
  const iceSourceRef = useRef(null);
  const icePathRef = useRef(null);
  const connectedAtRef = useRef(null);

  const setPhaseSafe = (p) => { phaseRef.current = p; setPhase(p); };

  const getWsUrl = useCallback(() => {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const base = `${proto}//${window.location.host}`;
    const qs = new URLSearchParams({ role });
    if (token) qs.set("token", token);
    if (access) qs.set("access", access);
    return `${base}/api/ws/call/${sessionId}?${qs.toString()}`;
  }, [sessionId, role, token, access]);

  const startTimer = () => {
    if (timerRef.current) return;
    startedAtRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setDurationSec(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 500);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearConnectTimeout = () => {
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
  };

  // ---- Call health diagnostics via pc.getStats() ----
  const stopStats = () => {
    if (statsTimerRef.current) { clearInterval(statsTimerRef.current); statsTimerRef.current = null; }
  };

  const pollStats = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc || !pc.getStats) return;
    try {
      const stats = await pc.getStats(null);
      let candidateType = null;
      let turnUsed = false;
      let rttMs = null;
      let jitterMs = null;
      let packetsLost = null;
      let remoteBytes = 0;
      let remoteAudioReceived = false;

      const byId = new Map();
      stats.forEach((r) => byId.set(r.id, r));

      stats.forEach((r) => {
        if (r.type === "candidate-pair" && (r.state === "succeeded" || r.nominated)) {
          const local = byId.get(r.localCandidateId);
          if (local) {
            candidateType = local.candidateType || null;
            if (candidateType === "relay") turnUsed = true;
          }
          if (typeof r.currentRoundTripTime === "number") rttMs = Math.round(r.currentRoundTripTime * 1000);
        }
        if (r.type === "inbound-rtp" && r.kind === "audio") {
          remoteAudioReceived = true;
          if (typeof r.jitter === "number") jitterMs = Math.round(r.jitter * 1000);
          if (typeof r.packetsLost === "number") packetsLost = r.packetsLost;
          if (typeof r.bytesReceived === "number") remoteBytes = r.bytesReceived;
        }
      });

      const now = Date.now();
      let bitrateKbps = null;
      if (lastStatsRef.current.ts && remoteBytes >= lastStatsRef.current.bytes) {
        const dt = (now - lastStatsRef.current.ts) / 1000;
        if (dt > 0.2) {
          bitrateKbps = Math.round(((remoteBytes - lastStatsRef.current.bytes) * 8) / 1000 / dt);
        }
      }
      lastStatsRef.current = { bytes: remoteBytes, ts: now };

      const localAudioLive = !!localStreamRef.current?.getAudioTracks?.().some((t) => t.readyState === "live" && t.enabled);

      setHealth((prev) => ({
        ...prev,
        connectionState: pc.connectionState || prev.connectionState,
        iceState: pc.iceConnectionState || prev.iceState,
        candidateType,
        turnUsed,
        rttMs,
        jitterMs,
        packetsLost,
        bitrateKbps,
        localAudioLive,
        remoteAudioReceived: remoteAudioReceived || prev.remoteAudioReceived,
      }));
    } catch (_) { /* ignore */ }
  }, []);

  const startStats = useCallback(() => {
    if (statsTimerRef.current) return;
    pollStats();
    statsTimerRef.current = setInterval(pollStats, 2000);
  }, [pollStats]);

  const teardown = useCallback((notifyPeer = true) => {
    // Bloque 1 — [CALL_SUMMARY]: duración real de la llamada conectada
    if (connectedAtRef.current) {
      const duration_sec = Math.max(1, Math.round((Date.now() - connectedAtRef.current) / 1000));
      _track(sessionId, role, "CALL_SUMMARY", {
        duration_sec,
        path: icePathRef.current || null,
        provider: iceSourceRef.current || null,
        ..._envInfo(),
      });
      connectedAtRef.current = null;
    }
    stopTimer();
    stopStats();
    clearConnectTimeout();
    try {
      if (notifyPeer && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "hangup" }));
      }
    } catch (_) { /* ignore */ }
    try { wsRef.current?.close(); } catch (_) { /* ignore */ }
    wsRef.current = null;
    try { pcRef.current?.close(); } catch (_) { /* ignore */ }
    pcRef.current = null;
    if (localStreamRef.current) {
      try { localStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
      localStreamRef.current = null;
    }
    peerReadyRef.current = false;
    pendingIceRef.current = [];
    lastStatsRef.current = { bytes: 0, ts: 0 };
    wsOpenRef.current = false;
    micReadyRef.current = false;
    offerSentRef.current = false;
    pendingRemoteOfferRef.current = null;
    iceRestartTriedRef.current = false;
  }, [sessionId, role]);

  const cleanup = teardown;

  const sendSignal = (payload) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  };

  const ensurePeer = useCallback((iceServers, iceTransportPolicy) => {
    if (pcRef.current) return pcRef.current;
    // Bloque D §19 — política de transporte ICE configurable por el servidor.
    // `all` (default actual) o `relay` (solo candidatos relay). Config inválida
    // o ausente → fallback seguro a `all`.
    const policy = iceTransportPolicy === "relay" ? "relay" : "all";
    const pc = new RTCPeerConnection({ iceServers, iceTransportPolicy: policy });
    _track(sessionId, role, "PC_CREATED", {
      ice_server_count: (iceServers || []).length,
      ice_server_urls: (iceServers || []).map((s) => s.urls),
      ice_transport_policy: policy,
    });
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        const cs = e.candidate.candidate || "";
        let kind = null;
        const m = / typ (\w+)/.exec(cs);
        if (m) kind = m[1];
        _track(sessionId, role, "ICE_CANDIDATE_GATHERED", {
          candidate_type: kind,
          protocol: e.candidate.protocol || null,
          relay_protocol: e.candidate.relayProtocol || null,
        });
        sendSignal({ type: "ice", candidate: e.candidate.toJSON() });
        _track(sessionId, role, "ICE_CANDIDATE_SENT", { candidate_type: kind });
      }
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      _track(sessionId, role, "PC_ICE_CONNECTION_STATE", { state: st });
      setHealth((h) => ({ ...h, iceState: st, connectionState: pc.connectionState }));
      sendSignal({ type: "state", state: st });
      if (st === "connected" || st === "completed") {
        clearConnectTimeout();
        iceRestartTriedRef.current = false;
        if (!connectedAtRef.current) connectedAtRef.current = Date.now();
        setPhaseSafe("connected");
        startTimer();
        startStats();
        // iter71 3.1 — [ICE_PATH_USED host|srflx|relay] per connected call
        setTimeout(async () => {
          try {
            const stats = await pc.getStats(null);
            const byId = new Map();
            stats.forEach((r) => byId.set(r.id, r));
            let path = null;
            stats.forEach((r) => {
              if (r.type === "candidate-pair" && (r.state === "succeeded" || r.nominated)) {
                const local = byId.get(r.localCandidateId);
                if (local) path = local.candidateType || path;
              }
            });
            icePathRef.current = path || icePathRef.current;
            _track(sessionId, role, "ICE_PATH_USED", { path: path || "unknown" });
            // Bloque 1 — [ICE_CANDIDATE_TYPE] con resultado, plataforma, red y proveedor
            _track(sessionId, role, "ICE_CANDIDATE_TYPE", {
              candidate_type: path || "unknown",
              result: "connected",
              provider: iceSourceRef.current || null,
              ..._envInfo(),
            });
          } catch (_) { /* observability only */ }
        }, 1500);
      } else if (st === "failed") {
        // iter71 3.3 — one automatic restartIce attempt before giving up
        if (!iceRestartTriedRef.current) {
          iceRestartTriedRef.current = true;
          _track(sessionId, role, "ICE_RESTART_ATTEMPTED", {});
          try {
            pc.restartIce();
            if (role === "scanner") {
              (async () => {
                try {
                  const offer = await pc.createOffer({ iceRestart: true });
                  await pc.setLocalDescription(offer);
                  sendSignal({ type: "offer", sdp: pc.localDescription.sdp });
                } catch (_) { /* fall through to timeout */ }
              })();
            }
            // Arm a fresh 10s window for the restart to succeed.
            clearConnectTimeout();
            connectTimeoutRef.current = setTimeout(() => {
              if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") return;
              _track(sessionId, role, "ICE_RESTART_RESULT", { result: "failed" });
              _track(sessionId, role, "ICE_CANDIDATE_TYPE", {
                candidate_type: icePathRef.current || null, result: "failed", reason: "ice_failed",
                provider: iceSourceRef.current || null, ..._envInfo(),
              });
              sendSignal({ type: "state", state: "failed", reason: "ice_failed" });
              setPhaseSafe("failed");
              setError("ice_failed");
            }, 10_000);
            return;
          } catch (_) { /* restartIce unsupported → fail below */ }
        }
        clearConnectTimeout();
        setPhaseSafe("failed");
        setError("ice_failed");
        _track(sessionId, role, "ICE_CANDIDATE_TYPE", {
          candidate_type: icePathRef.current || null, result: "failed", reason: "ice_failed",
          provider: iceSourceRef.current || null, ..._envInfo(),
        });
        sendSignal({ type: "state", state: "failed", reason: "ice_failed" });
      } else if (st === "disconnected") {
        // Brief drop — let onclose decide
      }
    };
    pc.onicegatheringstatechange = () => {
      _track(sessionId, role, "PC_ICE_GATHERING_STATE", { state: pc.iceGatheringState });
    };
    pc.onconnectionstatechange = () => {
      _track(sessionId, role, "PC_CONNECTION_STATE", { state: pc.connectionState });
      setHealth((h) => ({ ...h, connectionState: pc.connectionState }));
    };
    pc.ontrack = (e) => {
      _track(sessionId, role, "ONTRACK_FIRED", {
        track_kind: e.track?.kind || null,
        stream_id: e.streams?.[0]?.id || null,
        stream_count: e.streams?.length || 0,
      });
      if (!remoteAudioRef.current) {
        const audio = new Audio();
        audio.autoplay = true;
        audio.playsInline = true;
        remoteAudioRef.current = audio;
      }
      remoteAudioRef.current.srcObject = e.streams[0];
      _track(sessionId, role, "REMOTE_AUDIO_ATTACHED", { stream_id: e.streams?.[0]?.id || null });
      _track(sessionId, role, "REMOTE_AUDIO_PLAY_CALLED");
      remoteAudioRef.current.play?.().catch((err) => {
        _track(sessionId, role, "REMOTE_AUDIO_PLAY_FAILED", {
          error_name: err?.name || null, error_message: (err?.message || "").slice(0, 120),
        });
      });
      setHealth((h) => ({ ...h, remoteAudioReceived: true }));
      sendSignal({ type: "audio_track", present: true, kind: e.track?.kind || "audio" });
    };
    pcRef.current = pc;
    return pc;
  }, [sessionId, role, startStats]);

  const start = useCallback(async () => {
    if (phaseRef.current === "connecting" || phaseRef.current === "connected") return;
    // Retry after failed/ended: drop the previous pc/ws WITHOUT hangup-ing the peer
    if (pcRef.current || wsRef.current) teardown(false);
    setError(null);
    setPhaseSafe("connecting");

    // iter61 2.3 — hard connect timeout. Never "Conectando..." forever.
    clearConnectTimeout();
    connectTimeoutRef.current = setTimeout(() => {
      if (phaseRef.current === "connected") return;
      _track(sessionId, role, "CALL_CONNECT_TIMEOUT", { timeout_seconds: CONNECT_TIMEOUT_SECONDS });
      _track(sessionId, role, "ICE_CANDIDATE_TYPE", {
        candidate_type: icePathRef.current || null, result: "failed", reason: "connect_timeout",
        provider: iceSourceRef.current || null, ..._envInfo(),
      });
      sendSignal({ type: "state", state: "failed", reason: "connect_timeout" });
      setPhaseSafe("failed");
      setError("connect_timeout");
    }, CONNECT_TIMEOUT_SECONDS * 1000);

    try {
      // 1. ICE servers (fetch first, cheap)
      let iceServers = [{ urls: "stun:stun.l.google.com:19302" }];
      let iceSource = null;
      let icePolicy = "all";
      try {
        const iceRes = await fetch(`/api/ws/call/${sessionId}/ice-servers`);
        const iceJson = await iceRes.json();
        iceServers = iceJson.ice_servers || iceServers;
        iceSource = iceJson.source || null;
        icePolicy = iceJson.ice_transport_policy === "relay" ? "relay" : "all";
      } catch (_) { /* keep STUN fallback */ }
      _track(sessionId, role, "ICE_SERVERS_FETCHED", {
        source: iceSource, count: iceServers.length,
        urls: iceServers.map((s) => s.urls),
        ice_transport_policy: icePolicy,
      });
      iceSourceRef.current = iceSource;
      setHealth((h) => ({ ...h, iceSource, icePolicy }));

      // 2. Peer BEFORE the WebSocket so handlers can be attached synchronously
      const pc = ensurePeer(iceServers, icePolicy);

      // Negotiation gate: offer goes out only when ws-open AND mic-ready.
      const maybeBeginNegotiation = () => {
        if (!wsOpenRef.current || !micReadyRef.current) return;
        const hasLocalAudio = !!localStreamRef.current?.getAudioTracks?.().length;
        sendSignal({ type: "audio_track", present: hasLocalAudio, kind: "audio", local: true });
        if (role === "scanner" && !offerSentRef.current) {
          offerSentRef.current = true;
          (async () => {
            try {
              const offer = await pc.createOffer();
              _track(sessionId, role, "LOCAL_OFFER_CREATED", { sdp_len: (offer.sdp || "").length });
              await pc.setLocalDescription(offer);
              _track(sessionId, role, "OFFER_SET_AS_LOCAL");
              sendSignal({ type: "offer", sdp: pc.localDescription.sdp });
            } catch (e) {
              clearConnectTimeout();
              setPhaseSafe("failed");
              setError("offer_failed");
              sendSignal({ type: "state", state: "failed", reason: "offer_failed" });
            }
          })();
        }
      };

      const handleRemoteOffer = async (msg) => {
        try {
          await pc.setRemoteDescription({ type: "offer", sdp: msg.sdp });
          _track(sessionId, role, "OFFER_SET_AS_REMOTE");
          for (const c of pendingIceRef.current) {
            try { await pc.addIceCandidate(c); } catch (_) { /* ignore */ }
          }
          pendingIceRef.current = [];
          const answer = await pc.createAnswer();
          _track(sessionId, role, "LOCAL_ANSWER_CREATED", { sdp_len: (answer.sdp || "").length });
          await pc.setLocalDescription(answer);
          _track(sessionId, role, "ANSWER_SET_AS_LOCAL");
          sendSignal({ type: "answer", sdp: pc.localDescription.sdp });
        } catch (e) {
          clearConnectTimeout();
          setPhaseSafe("failed");
          setError("answer_failed");
        }
      };

      // 3. WebSocket — ALL handlers attached synchronously (iter61 fix #1).
      const ws = new WebSocket(getWsUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        wsOpenRef.current = true;
        _track(sessionId, role, "WS_OPEN");
        _track(sessionId, role, "WS_OPEN_RESULT", { result: "ok" });
        sendSignal({ type: "state", state: "connecting" });
        maybeBeginNegotiation();
      };

      ws.onmessage = async (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }

        if (msg.type === "peer-joined") {
          peerReadyRef.current = true;
          // Belt-and-suspenders renegotiation: only once mic is ready.
          if (role === "scanner" && micReadyRef.current && pc.signalingState === "stable") {
            try {
              const offer = await pc.createOffer({ iceRestart: false });
              _track(sessionId, role, "LOCAL_OFFER_CREATED", {
                sdp_len: (offer.sdp || "").length, reason: "peer_joined_renegotiate",
              });
              await pc.setLocalDescription(offer);
              _track(sessionId, role, "OFFER_SET_AS_LOCAL", { reason: "peer_joined_renegotiate" });
              sendSignal({ type: "offer", sdp: pc.localDescription.sdp });
              offerSentRef.current = true;
            } catch (_) { /* if it fails, the buffered offer still gets through */ }
          }
        }
        if (msg.type === "hello") {
          peerReadyRef.current = !!msg.peer_present;
        }
        if (msg.type === "offer" && role === "owner") {
          _track(sessionId, role, "REMOTE_OFFER_RECEIVED", {
            sdp_len: (msg.sdp || "").length, queued: !micReadyRef.current,
          });
          if (!micReadyRef.current) {
            // Mic not acquired yet — answering now would produce one-way audio.
            pendingRemoteOfferRef.current = msg;
          } else {
            await handleRemoteOffer(msg);
          }
        }
        if (msg.type === "answer" && role === "scanner") {
          _track(sessionId, role, "REMOTE_ANSWER_RECEIVED", { sdp_len: (msg.sdp || "").length });
          try {
            await pc.setRemoteDescription({ type: "answer", sdp: msg.sdp });
            _track(sessionId, role, "ANSWER_SET_AS_REMOTE");
            for (const c of pendingIceRef.current) {
              try { await pc.addIceCandidate(c); } catch (_) { /* ignore */ }
            }
            pendingIceRef.current = [];
          } catch (e) {
            clearConnectTimeout();
            setPhaseSafe("failed");
            setError("set_answer_failed");
          }
        }
        if (msg.type === "ice") {
          const cand = msg.candidate;
          if (!cand) return;
          const cs = cand.candidate || "";
          let kind = null;
          const m = / typ (\w+)/.exec(cs);
          if (m) kind = m[1];
          _track(sessionId, role, "REMOTE_ICE_CANDIDATE_RECEIVED", { candidate_type: kind });
          if (!pc.remoteDescription) {
            pendingIceRef.current.push(cand);
          } else {
            try {
              await pc.addIceCandidate(cand);
              _track(sessionId, role, "REMOTE_ICE_CANDIDATE_ADDED", { candidate_type: kind });
            } catch (_) { /* ignore */ }
          }
        }
        if (msg.type === "hangup" || msg.type === "peer-left") {
          setPhaseSafe("ended");
          teardown(false);
        }
      };

      ws.onerror = () => {
        _track(sessionId, role, "WS_ERROR");
        if (!wsOpenRef.current) {
          _track(sessionId, role, "WS_OPEN_RESULT", { result: "error" });
        }
        if (phaseRef.current !== "connected") {
          clearConnectTimeout();
          setPhaseSafe("failed");
          setError("ws_error");
        }
      };
      ws.onclose = (ev) => {
        _track(sessionId, role, "WS_CLOSE", { code: ev?.code || null, reason: (ev?.reason || "").slice(0, 80) });
        if (!wsOpenRef.current) {
          _track(sessionId, role, "WS_OPEN_RESULT", { result: "closed_before_open", code: ev?.code || null });
        }
        if (phaseRef.current === "connecting") {
          clearConnectTimeout();
          setPhaseSafe("failed");
          setError("ws_closed");
        }
      };

      // 4. Mic (may throw NotAllowedError) — WS events can no longer be lost
      //    while we wait here, handlers are already attached.
      let stream;
      _track(sessionId, role, "GETUSERMEDIA_REQUESTED", { audio: true, video: false });
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch (micErr) {
        _track(sessionId, role, "GETUSERMEDIA_DENIED", {
          error_name: micErr?.name || null,
          error_message: (micErr?.message || "").slice(0, 120),
        });
        clearConnectTimeout();
        setPhaseSafe("failed");
        setError(micErr.name === "NotAllowedError" ? "mic_denied" : "mic_unavailable");
        teardown(false);
        return;
      }
      _track(sessionId, role, "GETUSERMEDIA_GRANTED", {
        track_count: stream.getTracks().length,
        audio_track_count: stream.getAudioTracks().length,
        audio_label: stream.getAudioTracks()[0]?.label || null,
      });
      localStreamRef.current = stream;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      setHealth((h) => ({ ...h, localAudioLive: stream.getAudioTracks().length > 0 }));
      micReadyRef.current = true;

      // Owner: an offer may have arrived (live or replayed) during mic wait.
      if (role === "owner" && pendingRemoteOfferRef.current) {
        const queued = pendingRemoteOfferRef.current;
        pendingRemoteOfferRef.current = null;
        await handleRemoteOffer(queued);
      }
      maybeBeginNegotiation();
    } catch (e) {
      clearConnectTimeout();
      setPhaseSafe("failed");
      setError(e.name === "NotAllowedError" ? "mic_denied" : "start_failed");
      teardown(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, sessionId, getWsUrl, teardown, ensurePeer]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
    sendSignal({ type: "mute", muted: next });
  }, [muted]);

  const hangup = useCallback(() => {
    setPhaseSafe("ended");
    cleanup();
  }, [cleanup]);

  useEffect(() => {
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoStart && sessionId && phase === "idle") start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, sessionId]);

  return { phase, muted, durationSec, error, start, toggleMute, hangup, health };
}
